import { useProductPageStore } from '../../contexts/ProductPageStore'
import { submitAnAnswer } from '../../contexts/fetches/product'
import { upvoteReview } from '../reviews/fetches/powerReviews'
import styles from './qna.module.scss'

import { useContext, useState } from 'react'

import { NotificationContext } from '@speedwaymotors/clutch/Contexts/NotificationContext/NotificationContext'
import classNames from 'classnames'

import { createClickedSegmentEvent } from 'Clutch/Utilities/Instrumentation/Impressions/impressionSegmentEvents'

import Area from 'Clutch/Molecules/Area'
import Button from 'Clutch/Molecules/Button'

import Card from 'Clutch/Atoms/Card'
import Panel from 'Clutch/Atoms/Panel'
import Svg from 'Clutch/Atoms/Svg'
import TextLink from 'Clutch/Atoms/TextLink'
import Typography from 'Clutch/Atoms/Typography'

const QnaCard = ({ qnaData, powerReviewsMerchantId, powerReviewsApiKey }) => {
  const [showAnswers, setShowAnswers] = useState(false)
  const [showAnswerForm, setShowAnswerForm] = useState(false)

  if (!qnaData) return false

  const answerCount = qnaData.answer?.length || 0
  const hasAnswers = answerCount > 0

  return (
    <Card fill>
      <div className={classNames(styles.inline, styles.marginBottom)}>
        <Svg size={0.875} icon={'help'} className={styles.noShrink} />
        <Typography font="bold" size="0.875">
          {qnaData.details.text}
        </Typography>
      </div>
      <div className={classNames(styles.inline, styles.marginBottom)}>
        <Typography tone="subtle" size="0.75">
          by {qnaData.details.nickname}
        </Typography>
        {qnaData.details.time_ago_string && (
          <Typography tone="subtle" size="0.75">
            • {qnaData.details.time_ago_string}
          </Typography>
        )}
      </div>
      <div className={classNames(styles.inline, styles.answerControls)}>
        <div className={styles.inline}>
          <Typography tone="subtle" size="0.75">
            {answerCount} {answerCount === 1 ? 'answer' : 'answers'}
          </Typography>
        </div>
        {hasAnswers && (
          <TextLink
            size={0.75}
            font="bold"
            segmentEvent={createClickedSegmentEvent('ToggleQnAAnswers')}
            onClick={() => setShowAnswers(!showAnswers)}
            className={styles.viewAnswerLink}
          >
            {showAnswers ? 'Hide' : `View ${answerCount === 1 ? 'Answer' : 'Answers'}`}
            <Svg size={0.625} icon={showAnswers ? 'chevron-up' : 'chevron-down'} />
          </TextLink>
        )}
      </div>
      {showAnswers && hasAnswers && (
        <div className={styles.addAnswerWrapper}>
          <div className={styles.addAnswerRow}>
            <Button
              intent={'subdued'}
              size={'small'}
              onClick={() => setShowAnswerForm(!showAnswerForm)}
              style={{ padding: '0.25rem 0.5rem', minWidth: 'fit-content' }}
            >
              <Svg size={0.5} icon={'plus'} intent={'subdued'} />
              <Typography size={0.75} font={'bold'} style={{ marginLeft: '0.25rem' }}>
                Add your answer
              </Typography>
            </Button>
          </div>

          <div className={styles.answerWrapper}>
            {qnaData.answer.map((ans, i) => (
              <AnswerCard
                key={`answer_${qnaData.ugc_id}_${i}`}
                answer={ans}
                powerReviewsMerchantId={powerReviewsMerchantId}
                powerReviewsApiKey={powerReviewsApiKey}
              />
            ))}
          </div>
        </div>
      )}
      {showAnswerForm && (
        <AnswerForm
          questionId={qnaData.ugc_id}
          onCancel={() => setShowAnswerForm(false)}
          onSuccess={() => setShowAnswerForm(false)}
        />
      )}
    </Card>
  )
}

const AnswerForm = ({ questionId, onCancel, onSuccess }) => {
  const [answerText, setAnswerText] = useState('')
  const [nickname, setNickname] = useState('')
  const [location, setLocation] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const { PushNotification } = useContext(NotificationContext)

  const skuBaseNumber = useProductPageStore((state) => state.skuBaseNumber)

  const handleSubmit = async () => {
    if (!answerText.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await submitAnAnswer(`${skuBaseNumber}`, `${questionId}`, answerText, nickname, email, location)
      setAnswerText('')
      setNickname('')
      setLocation('')
      if (onSuccess) {
        PushNotification('Thank you! Your answer has been submitted.', null, 3000, null, {
          icon: 'check',
          intent: 'positive',
        })
        onSuccess()
      }
    } catch (err) {
      console.error('Failed to submit answer:', err)
      setError('Failed to submit answer. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.answerFormWrapper}>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="qna_answer">
          <Typography size="0.75" font="bold">
            Answer:
          </Typography>
          <Typography size="0.75" font="bold" intent="warning">
            *
          </Typography>
        </label>
        <textarea
          id="qna_answer"
          className={styles.formTextarea}
          rows={4}
          value={answerText}
          onChange={(e) => setAnswerText(e.target.value)}
        />
      </div>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="qna_nickname">
          <Typography size="0.75" font="bold">
            Nickname:
          </Typography>
          <Typography size="0.75" font="bold" intent="warning">
            *
          </Typography>
        </label>
        <input
          id="qna_nickname"
          type="text"
          className={styles.formInput}
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="Ex. Nancy the Newbie"
        />
      </div>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="qna_location">
          <Typography size="0.75" font="bold">
            Your Location:
          </Typography>
          <Typography size="0.75" font="bold" intent="warning">
            *
          </Typography>
        </label>
        <input
          id="qna_location"
          type="text"
          className={styles.formInput}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Ex. Barstow, CA"
        />
      </div>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="qna_location">
          <Typography size="0.75" font="bold">
            Email:
          </Typography>
          <Typography size="0.75" font="bold" intent="warning">
            *
          </Typography>
        </label>
        <input
          id="qna_email"
          type="text"
          className={styles.formInput}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      {error && (
        <Typography size="0.75" tone="contrast">
          {error}
        </Typography>
      )}
      <div style={{ display: 'flex' }}>
        <Typography size="0.625" tone="warning">
          *
        </Typography>
        <Typography size="0.625" tone="subtle">
          Required field
        </Typography>
      </div>
      <div className={styles.formActions}>
        <Button intent={'subdued'} size={'small'} text={'Cancel'} onClick={onCancel} />
        <Button
          intent={'action'}
          size={'small'}
          text={submitting ? 'Submitting...' : 'Submit Answer'}
          segmentEvent={createClickedSegmentEvent('SubmitAnswer')}
          disabled={!answerText.trim() || !nickname.trim() || !location.trim() || !email.trim() || submitting}
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}

const AnswerCard = ({ answer, powerReviewsMerchantId, powerReviewsApiKey }) => {
  if (!answer?.details) return false

  return (
    <div className={styles.answerCard}>
      <div className={styles.answerHeader}>
        <div className={styles.inline}>
          {answer.details?.nickname && (
            <Typography font="bold" tone="subtle" size="0.75">
              {answer.details.nickname}
            </Typography>
          )}
          {answer.details?.is_expert && <ExpertBadge />}
          {answer.details?.is_verified && <VerifiedBadge />}
        </div>
      </div>
      <Typography size="0.875" className={styles.marginBottom}>
        {answer.details?.text}
      </Typography>
      <HelpfulButton
        ugcId={answer.ugc_id}
        helpfulVotes={answer.metrics?.helpful_votes}
        powerReviewsMerchantId={powerReviewsMerchantId}
        powerReviewsApiKey={powerReviewsApiKey}
      />
    </div>
  )
}

const VerifiedBadge = () => {
  return (
    <Area intent="positive" noPadding fitContent>
      <div className={styles.inline}>
        <Svg size="0.75" intent="positive" icon="check" />
        <Typography font="bold" size="0.75">
          Verified
        </Typography>
      </div>
    </Area>
  )
}

const ExpertBadge = () => {
  return (
    <Area intent="action" noPadding fitContent>
      <div className={styles.inline}>
        <Svg size="0.75" intent="action" icon="check" />
        <Typography font="bold" size="0.75">
          Expert
        </Typography>
      </div>
    </Area>
  )
}

const HelpfulButton = ({ ugcId, helpfulVotes, powerReviewsMerchantId, powerReviewsApiKey }) => {
  const [voted, setVoted] = useState(false)

  if (!ugcId || !powerReviewsMerchantId || !powerReviewsApiKey) return false

  return (
    <Panel layer={0}>
      <div disabledbool={voted.toString()} className={styles.helpfulButtonWrapper}>
        <Button
          intent={'subdued'}
          segmentEvent={createClickedSegmentEvent('ReviewUpvote')}
          size={'small'}
          className={classNames(styles.inline, styles.helpfulButton)}
          disabled={voted}
          onClick={() => {
            setVoted(true)
            //upvoteReview(ugcId, powerReviewsMerchantId, powerReviewsApiKey)
          }}
        >
          <Svg size={0.875} icon={'thumb_up'} brand={'secondarypalette_black'} />
          <Typography size={0.875} font={'bold'}>
            {voted ? helpfulVotes + 1 : helpfulVotes}
          </Typography>
        </Button>
      </div>
    </Panel>
  )
}

export default QnaCard
